import type { LucideProps } from 'lucide-react';
import dynamic from 'next/dynamic';
import type { ComponentType, RefAttributes, SVGProps } from 'react';

type SvgAttributes = Partial<SVGProps<SVGSVGElement>>;
type ElementAttributes = RefAttributes<SVGSVGElement> & SvgAttributes;

const FortuneIconName = [
  'ButtonDown',
  'ButtonLeft',
  'ButtonMinus',
  'ButtonPencil',
  'ButtonPlus',
  'ButtonRight',
  'ButtonSearch',
  'ButtonShare',
  'ButtonUp',
  'Facebook',
  'FormInfo',
  'FormPassword',
  'Fortune500Logo',
  'Instagram',
  'LinkedIn',
  'Pinterest',
  'Twitter',
] as const;

const LucideIconName = [
  'BadgeCheck',
  'Bookmark',
  'Check',
  'ChevronDown',
  'ChevronLeft',
  'ChevronRight',
  'ChevronUp',
  'CircleCheck',
  'CircleMinus',
  'CirclePlay',
  'CirclePlus',
  'CircleUserRound',
  'CircleX',
  'Clock',
  'Dot',
  'Info',
  'ListFilter',
  'Lock',
  'LockKeyhole',
  'Mail',
  'Menu',
  'Minus',
  'Pencil',
  'Plus',
  'RefreshCw',
  'Search',
  'Share2',
  'SquareArrowOutUpRight',
  'Trash2',
  'TriangleAlert',
  'Upload',
  'X',
] as const;

export type DynamicLucideIcon = ComponentType<LucideProps>;

export interface FortuneIconProps extends ElementAttributes {
  size?: string | number;
}

export type AllIconProps = LucideProps | FortuneIconProps;

export type FortuneIconName = (typeof FortuneIconName)[number];
export type LucideIconName = (typeof LucideIconName)[number];
export type IconName = LucideIconName | FortuneIconName;

export type DynamicFortuneIcon = ComponentType<FortuneIconProps>;
export type DynamicIcon = ComponentType<AllIconProps>;

export type FortuneIconMap = {
  [Key in FortuneIconName]: DynamicFortuneIcon;
};

export type LucideIconMap = {
  [Key in LucideIconName]: DynamicLucideIcon;
};

export type IconMap = {
  [Key in IconName]: DynamicIcon;
};

// biome-ignore-start lint/style/useNamingConvention: <using lucide component names>
export const lucideIconMap: LucideIconMap = {
  BadgeCheck: dynamic(() => import('lucide-react').then((mod) => mod.BadgeCheck)),
  Bookmark: dynamic(() => import('lucide-react').then((mod) => mod.Bookmark)),
  Check: dynamic(() => import('lucide-react').then((mod) => mod.Check)),
  ChevronDown: dynamic(() => import('lucide-react').then((mod) => mod.ChevronDown)),
  ChevronLeft: dynamic(() => import('lucide-react').then((mod) => mod.ChevronLeft)),
  ChevronRight: dynamic(() => import('lucide-react').then((mod) => mod.ChevronRight)),
  ChevronUp: dynamic(() => import('lucide-react').then((mod) => mod.ChevronUp)),
  CircleCheck: dynamic(() => import('lucide-react').then((mod) => mod.CircleCheck)),
  CircleMinus: dynamic(() => import('lucide-react').then((mod) => mod.CircleMinus)),
  CirclePlay: dynamic(() => import('lucide-react').then((mod) => mod.CirclePlay)),
  CirclePlus: dynamic(() => import('lucide-react').then((mod) => mod.CirclePlus)),
  CircleUserRound: dynamic(() => import('lucide-react').then((mod) => mod.CircleUserRound)),
  CircleX: dynamic(() => import('lucide-react').then((mod) => mod.CircleX)),
  Clock: dynamic(() => import('lucide-react').then((mod) => mod.Clock)),
  Dot: dynamic(() => import('lucide-react').then((mod) => mod.Dot)),
  Info: dynamic(() => import('lucide-react').then((mod) => mod.Info)),
  ListFilter: dynamic(() => import('lucide-react').then((mod) => mod.ListFilter)),
  Lock: dynamic(() => import('lucide-react').then((mod) => mod.Lock)),
  LockKeyhole: dynamic(() => import('lucide-react').then((mod) => mod.LockKeyhole)),
  Mail: dynamic(() => import('lucide-react').then((mod) => mod.Mail)),
  Menu: dynamic(() => import('lucide-react').then((mod) => mod.Menu)),
  Minus: dynamic(() => import('lucide-react').then((mod) => mod.Minus)),
  Pencil: dynamic(() => import('lucide-react').then((mod) => mod.Pencil)),
  Plus: dynamic(() => import('lucide-react').then((mod) => mod.Plus)),
  RefreshCw: dynamic(() => import('lucide-react').then((mod) => mod.RefreshCw)),
  Search: dynamic(() => import('lucide-react').then((mod) => mod.Search)),
  Share2: dynamic(() => import('lucide-react').then((mod) => mod.Share2)),
  SquareArrowOutUpRight: dynamic(() => import('lucide-react').then((mod) => mod.SquareArrowOutUpRight)),
  Trash2: dynamic(() => import('lucide-react').then((mod) => mod.Trash2)),
  TriangleAlert: dynamic(() => import('lucide-react').then((mod) => mod.TriangleAlert)),
  Upload: dynamic(() => import('lucide-react').then((mod) => mod.Upload)),
  X: dynamic(() => import('lucide-react').then((mod) => mod.X)),
};
// biome-ignore-end lint/style/useNamingConvention: <>

// biome-ignore-start lint/style/useNamingConvention: <using component names>
export const fortuneIconMap: FortuneIconMap = {
  ButtonDown: dynamic(() => import('@/app_components/icons/buttons/ButtonDown').then((mod) => mod.ButtonDown)),
  ButtonLeft: dynamic(() => import('@/app_components/icons/buttons/ButtonLeft').then((mod) => mod.ButtonLeft)),
  ButtonMinus: dynamic(() => import('@/app_components/icons/buttons/ButtonMinus').then((mod) => mod.ButtonMinus)),
  ButtonPencil: dynamic(() => import('@/app_components/icons/buttons/ButtonPencil').then((mod) => mod.ButtonPencil)),
  ButtonPlus: dynamic(() => import('@/app_components/icons/buttons/ButtonPlus').then((mod) => mod.ButtonPlus)),
  ButtonRight: dynamic(() => import('@/app_components/icons/buttons/ButtonRight').then((mod) => mod.ButtonRight)),
  ButtonSearch: dynamic(() => import('@/app_components/icons/buttons/ButtonSearch').then((mod) => mod.ButtonSearch)),
  ButtonShare: dynamic(() => import('@/app_components/icons/buttons/ButtonShare').then((mod) => mod.ButtonShare)),
  ButtonUp: dynamic(() => import('@/app_components/icons/buttons/ButtonUp').then((mod) => mod.ButtonUp)),
  Facebook: dynamic(() => import('@/app_components/icons/socials/Facebook').then((mod) => mod.Facebook)),
  FormInfo: dynamic(() => import('@/app_components/icons/forms/FormInfo').then((mod) => mod.FormInfo)),
  FormPassword: dynamic(() => import('@/app_components/icons/forms/FormPassword').then((mod) => mod.FormPassword)),
  Fortune500Logo: dynamic(() =>
    import('@/app_components/icons/logos/Fortune500Logo').then((mod) => mod.Fortune500Logo),
  ),
  Instagram: dynamic(() => import('@/app_components/icons/socials/Instagram').then((mod) => mod.Instagram)),
  LinkedIn: dynamic(() => import('@/app_components/icons/socials/LinkedIn').then((mod) => mod.LinkedIn)),
  Pinterest: dynamic(() => import('@/app_components/icons/socials/Pinterest').then((mod) => mod.Pinterest)),
  Twitter: dynamic(() => import('@/app_components/icons/socials/Twitter').then((mod) => mod.Twitter)),
};
// biome-ignore-end lint/style/useNamingConvention: <>
